import { Box, Dialog, DialogActions, DialogContent, styled, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconCross } from 'assets/img';
import { LwButton, LwIconButton } from 'redesign';
import { useAlert } from 'shared/components/alerts';
import { AddClaForm } from './add-cla-form';
import { mapFormDataToPayload } from './add-cla-dialog.mappers';
import { AddClaFormValues } from './add-cla-dialog.types';
import { useCreatePartnerWorkerCla } from '../hooks/use-create-partner-worker-cla';

export type AddClaDialogProps = {
  workerId: string;
  open: boolean;
  onClose: () => void;
};

const DEFAULT_VALUES: AddClaFormValues = {
  name: '',
  scales: [],
};

export const AddClaDialog = ({ workerId, open, onClose }: AddClaDialogProps) => {
  const { t } = useTranslation(['partner-screen-specific', 'web-common']);
  const { alertSuccess, alertError } = useAlert();
  const mutation = useCreatePartnerWorkerCla();

  const formMethods = useForm<AddClaFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const handleClose = () => {
    formMethods.reset(DEFAULT_VALUES);
    onClose();
  };

  const handleSubmit = formMethods.handleSubmit((data) => {
    mutation.mutate(
      { partnerWorkerId: workerId, payload: mapFormDataToPayload(data) },
      {
        onSuccess: () => {
          alertSuccess(
            t('partner-worker-detail.add-cla-dialog.created.success', {
              ns: 'partner-screen-specific',
            })
          );
          handleClose();
        },
        onError: (error) => alertError(error),
      }
    );
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="add-cla-dialog-title"
    >
      <TitleBar>
        <TitleTextWrapper>
          <TitleText id="add-cla-dialog-title" variant="h2">
            {t('partner-worker-detail.add-cla-dialog.title', { ns: 'partner-screen-specific' })}
          </TitleText>
          <SubtitleText variant="body2">
            {t('partner-worker-detail.add-cla-dialog.subtitle', { ns: 'partner-screen-specific' })}
          </SubtitleText>
        </TitleTextWrapper>
        <LwIconButton onClick={handleClose} color="secondary" iconColorMode="stroke" size="medium">
          <IconCross />
        </LwIconButton>
      </TitleBar>
      <StyledDialogContent>
        <FormProvider {...formMethods}>
          <AddClaForm />
        </FormProvider>
      </StyledDialogContent>
      <DialogActionsWrapper>
        <LwButton onClick={handleClose} color="secondary" iconColorMode="fill">
          {t('buttons.cancel', { ns: 'web-common' })}
        </LwButton>
        <LwButton
          onClick={handleSubmit}
          color="primary"
          iconColorMode="fill"
          isLoading={mutation.isLoading}
        >
          {t('buttons.save', { ns: 'web-common' })}
        </LwButton>
      </DialogActionsWrapper>
    </Dialog>
  );
};

const TitleBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  paddingBottom: theme.spacing(3),
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(0, 0, 2, 0),
}));

const TitleTextWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const TitleText = styled(Typography)({
  margin: 0,
});

const SubtitleText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(1),
}));

const DialogActionsWrapper = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(3, 0, 0),
}));
